import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody,
  Button, FormControl, FormLabel, Input, Select, Flex, Text, Alert, AlertIcon,
  Switch, Divider,
} from '@chakra-ui/react';
import * as FiIcons from 'react-icons/fi';
import { updateContentCreator } from '../../../../../store/contentCreators/contentCreatorsSlice';
import theme from '../../../../global/theme';
import { backendUrl } from '../../../../../utilities/axios';

const PLATFORMS = ['youtube', 'twitch', 'discord', 'facebook', 'instagram', 'tiktok'];

const UpdateModal = ({ data, onClose }) => {
  const dispatch = useDispatch();
  const contentCreators = useSelector(state => state.contentCreators);
  const { register, handleSubmit, formState: { errors } } = useForm({ defaultValues: data });
  const [platformRows, setPlatformRows] = useState(data.platforms || []);

  const addPlatform = () => setPlatformRows(prev => [...prev, { type: 'youtube', url: '', subs: '' }]);
  const removePlatform = (i) => setPlatformRows(prev => prev.filter((_, idx) => idx !== i));
  const updatePlatform = (i, field, value) =>
    setPlatformRows(prev => prev.map((p, idx) => idx === i ? { ...p, [field]: value } : p));

  const onSubmit = (values) => {
    const fd = new FormData();
    fd.append('_id', data._id);
    fd.append('prev_avatar', data.avatar || '');
    if (values.avatar?.[0]) fd.append('avatar', values.avatar[0]);
    fd.append('prev_banner', data.banner || '');
    if (values.banner?.[0]) fd.append('banner', values.banner[0]);
    fd.append('name', values.name);
    fd.append('tag', values.tag || '');
    fd.append('primary_platform', values.primary_platform);
    fd.append('followers', values.followers || '0');
    fd.append('videos_count', values.videos_count || 0);
    fd.append('joined_year', values.joined_year || '');
    fd.append('platforms', JSON.stringify(platformRows));
    fd.append('showInHome', values.showInHome ?? false);
    fd.append('is_verified', values.is_verified ?? false);
    dispatch(updateContentCreator(fd)).unwrap().then(() => onClose());
  };

  return (
    <Modal isOpen onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent borderRadius={4} paddingBlock={4} bg={theme.dark}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader textAlign="center" color="orange" fontSize={20}>تعديل صانع المحتوى</ModalHeader>
          <ModalBody>
            {contentCreators.errors.length > 0 && (
              <Alert status="error" variant="left-accent" marginBottom={4}>
                <AlertIcon />
                {contentCreators.errors.map((e, i) => <Text key={i}>{e?.msg}</Text>)}
              </Alert>
            )}
            <FormControl mb={4}>
              <FormLabel color={theme.light}>الاسم *</FormLabel>
              <Input bg={theme.light} border="none" borderRadius={4}
                {...register('name', { required: 'مطلوب' })} />
              {errors.name && <Text color="red.400" mt={1}>{errors.name.message}</Text>}
            </FormControl>
            <Flex gap={4} mb={4}>
              <FormControl>
                <FormLabel color={theme.light}>التاق</FormLabel>
                <Input bg={theme.light} border="none" borderRadius={4} {...register('tag')} />
              </FormControl>
              <FormControl>
                <FormLabel color={theme.light}>المنصة الرئيسية</FormLabel>
                <Select bg={theme.light} border="none" borderRadius={4} {...register('primary_platform')}>
                  {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
                </Select>
              </FormControl>
            </Flex>
            <Flex gap={4} mb={4}>
              <FormControl>
                <FormLabel color={theme.light}>المتابعون</FormLabel>
                <Input bg={theme.light} border="none" borderRadius={4} {...register('followers')} />
              </FormControl>
              <FormControl>
                <FormLabel color={theme.light}>عدد الفيديوهات</FormLabel>
                <Input type="number" bg={theme.light} border="none" borderRadius={4} {...register('videos_count')} />
              </FormControl>
              <FormControl>
                <FormLabel color={theme.light}>سنة الانضمام</FormLabel>
                <Input bg={theme.light} border="none" borderRadius={4} {...register('joined_year')} />
              </FormControl>
            </Flex>
            <FormControl mb={4}>
              <FormLabel color={theme.light}>صورة جديدة (اختياري)</FormLabel>
              <Input type="file" accept="image/*" bg={theme.light} border="none" borderRadius={4}
                p={1} {...register('avatar')} />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel color={theme.light}>صورة الغلاف الجديدة (Banner، اختياري)</FormLabel>
              {data.banner && (
                <img src={`${backendUrl}/creators/${data.banner}`} alt="banner"
                  style={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 4, marginBottom: 8 }} />
              )}
              <Input type="file" accept="image/*" bg={theme.light} border="none" borderRadius={4}
                p={1} {...register('banner')} />
            </FormControl>

            <Divider borderColor="whiteAlpha.100" mb={4} />
            <Flex gap={6} mb={4} flexWrap="wrap">
              <FormControl display="flex" alignItems="center" width="auto">
                <FormLabel color="cyan.300" mb={0} fontSize={13}>عرض في الرئيسية</FormLabel>
                <Switch colorScheme="cyan" {...register('showInHome')} defaultChecked={data.showInHome} />
              </FormControl>
              <FormControl display="flex" alignItems="center" width="auto">
                <FormLabel color="green.300" mb={0} fontSize={13}>قناة موثقة</FormLabel>
                <Switch colorScheme="green" {...register('is_verified')} defaultChecked={data.is_verified} />
              </FormControl>
            </Flex>
            <Divider borderColor="whiteAlpha.100" mb={4} />
            <Text color={theme.light} fontWeight="bold" mb={2}>المنصات</Text>
            {platformRows.map((p, i) => (
              <Flex key={i} gap={3} mb={3} alignItems="center">
                <Select value={p.type} onChange={e => updatePlatform(i, 'type', e.target.value)}
                  bg={theme.light} border="none" borderRadius={4} w="150px">
                  {PLATFORMS.map(pl => <option key={pl} value={pl}>{pl}</option>)}
                </Select>
                <Input placeholder="رابط" value={p.url} onChange={e => updatePlatform(i, 'url', e.target.value)}
                  bg={theme.light} border="none" borderRadius={4} />
                <Input placeholder="المتابعون" value={p.subs} onChange={e => updatePlatform(i, 'subs', e.target.value)}
                  bg={theme.light} border="none" borderRadius={4} w="100px" />
                <Button size="sm" colorScheme="red" onClick={() => removePlatform(i)}>
                  <FiIcons.FiMinus />
                </Button>
              </Flex>
            ))}
            <Button size="sm" colorScheme="blue" onClick={addPlatform} mb={4}>
              <FiIcons.FiPlus /> إضافة منصة
            </Button>
          </ModalBody>
          <ModalFooter>
            <Flex justifyContent="flex-end" gap={3}>
              <Button type="submit" rightIcon={<FiIcons.FiSave />} colorScheme="green"
                isLoading={contentCreators.isLoading}>حفظ</Button>
              <Button type="button" rightIcon={<FiIcons.FiX />} colorScheme="red"
                isLoading={contentCreators.isLoading} onClick={onClose}>إلغاء</Button>
            </Flex>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default UpdateModal;
